import { useCallback, useEffect, useMemo, useRef } from "react";

import { useLocation, useParams } from "react-router";

import { useAttributionGroups } from "../../../../Components/hooks/cloudAnalytics/attributionGroups/useAttributionGroups";
import { useAttributionsContext } from "../../../../Context/AttributionsContext";
import { useAuthContext } from "../../../../Context/AuthContext";
import { type AttributionWRef } from "../../../../types";
import useSegmentTrackEvent from "../../../../utils/useSegmentTrackEvent";
import { isEditor } from "../../utilities";
import { type AttributionGroupWithRef } from "../types";
import { trackAllocationEvent } from "../utils";
import useAllocationDndStore from "./useAllocationDnd";
import { formStoreManager } from "./useAllocationFormState";

type Allocations = Array<AttributionGroupWithRef | AttributionWRef>;

const isAttributionGroup = (item: AttributionWRef | AttributionGroupWithRef): item is AttributionGroupWithRef =>
  "attributions" in item.data;

export const useAllocationSetup = () => {
  const { allocationId: allocation } = useParams<{ allocationId: string }>();
  const { state } = useLocation<{
    allocationId: string;
    type: "duplicate" | "create" | "edit";
  }>();
  const allocationId = allocation || state?.allocationId;

  const { attributions: filteredAttributions } = useAttributionsContext();
  const [attributionGroups] = useAttributionGroups();
  const { trackEvent } = useSegmentTrackEvent();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const {
    addItem,
    clear,
    setHasUnallocated,
    setUnallocated,
    setAllocationName,
    hasUnallocated,
    items,
    setIsGroup,
    setIsAllocationEditor,
    resetItemsOriginal,
    setUnallocatedOriginal,
    setAllocationDescription,
  } = useAllocationDndStore();

  const hasTrackedRef = useRef(false);

  const findAttributionById = useCallback(
    (attributions: Allocations, id: string): AttributionGroupWithRef | AttributionWRef | undefined =>
      attributions?.find((item) => item?.ref.id === id),
    []
  );

  const allAttributions = useMemo(
    () => [...filteredAttributions, ...attributionGroups],
    [filteredAttributions, attributionGroups]
  );

  const currentAllocation = useMemo(
    () => findAttributionById(allAttributions, allocationId),
    [findAttributionById, allAttributions, allocationId]
  );

  const mapAttributionsToGroup = useCallback(
    (attributionIds: Array<{ id: string }>, filteredAttributions: AttributionWRef[]): AttributionWRef[] => {
      if (!attributionIds?.length || !filteredAttributions?.length) return [];

      return attributionIds
        .map((item) => findAttributionById(filteredAttributions, item.id))
        .filter((item): item is AttributionWRef => item !== undefined);
    },
    [findAttributionById]
  );

  const allocations = useMemo((): AttributionWRef[] => {
    if (!currentAllocation) {
      return [];
    }

    if (isAttributionGroup(currentAllocation)) {
      const { attributions, nullFallback } = currentAllocation.data;
      setIsGroup(true);

      if (nullFallback) {
        setUnallocated(nullFallback);
        setUnallocatedOriginal(nullFallback);
        setHasUnallocated(true);
      }

      return mapAttributionsToGroup(attributions, filteredAttributions);
    }

    return [currentAllocation];
  }, [
    currentAllocation,
    filteredAttributions,
    mapAttributionsToGroup,
    setHasUnallocated,
    setIsGroup,
    setUnallocated,
    setUnallocatedOriginal,
  ]);

  const setupAllocationsStore = useCallback(
    (allocations: AttributionWRef[]) => {
      const isDuplicate = state?.type === "duplicate";
      if (!allocations?.length) return;
      if (isDuplicate) {
        setAllocationName(`${currentAllocation?.data.name} (copy)`);
      } else {
        setIsAllocationEditor(
          isEditor(currentUser.email, { collaborators: currentAllocation?.data?.collaborators || [] })
        );
        setAllocationName(currentAllocation?.data.name || "");
      }
      setAllocationDescription(currentAllocation?.data.description);
      allocations.forEach((item: AttributionWRef, index, arr) => {
        const isSingle = arr.length === 1;
        const storeId = crypto.randomUUID();
        addItem(storeId);
        const { filters, formula, name } = item.data;
        const newStore = formStoreManager.getStore(storeId);
        const { setEditMode, setSelectedExistingRule, setViewType, setIsDirty } = newStore.getState();
        const values = {
          id: item.ref.id,
          name,
          filterFields: filters || [],
          formula: formula || "",
        };

        if (state?.type === "duplicate") {
          setViewType(isSingle ? "new" : "existing", { name, filterFields: filters || [], formula: formula || "" });
          !isSingle && setSelectedExistingRule(item);
        } else {
          setEditMode(values);
          setSelectedExistingRule(item);
          setViewType("existing");
        }
        setIsDirty(true);
      });
      resetItemsOriginal();
    },
    [
      addItem,
      currentAllocation?.data?.collaborators,
      currentAllocation?.data.description,
      currentAllocation?.data.name,
      currentUser.email,
      resetItemsOriginal,
      setAllocationDescription,
      setAllocationName,
      setIsAllocationEditor,
      state?.type,
    ]
  );

  // Initialize allocations
  useEffect(() => {
    if (allocationId && allocations.length > 0) {
      setupAllocationsStore(allocations);
    } else if (!allocationId) {
      addItem(crypto.randomUUID());
    }
    if (currentAllocation && isAttributionGroup(currentAllocation)) {
      setIsGroup(true);
    }
    return () => {
      clear();
    };
  }, [addItem, allocationId, allocations, clear, currentAllocation, setIsGroup, setupAllocationsStore]);

  // Track allocation event
  useEffect(() => {
    if (items.main.length > 0 && !hasTrackedRef.current && currentAllocation) {
      hasTrackedRef.current = true;
      trackAllocationEvent({
        trackEvent,
        eventName: "Allocation Opened",
        ruleCount: items.main.length,
        hasUnallocated,
        allocationId,
        collaborators: currentAllocation.data.collaborators,
        currentUserEmail: currentUser.email,
      });
    }
  }, [allocationId, currentAllocation, currentUser.email, hasUnallocated, items.main.length, trackEvent]);

  return {
    allocationId,
    items,
  };
};
